import { useState, useEffect, useCallback } from "react";
import type { DefaultTagDefinition } from "../../types/tags";
import { CheckIcon } from "../../assets/CheckIcon";
import { ModalCloseButton } from "../ModalCloseButton";
import "./TagSelectorModal.css";

interface TagSelectorModalProps {
  visible: boolean;
  definition: DefaultTagDefinition | null;
  selectedValues: string[];
  onSave: (values: string[]) => void;
  onClose: () => void;
}

export function TagSelectorModal({
  visible,
  definition,
  selectedValues,
  onSave,
  onClose,
}: TagSelectorModalProps) {
  const [localSelection, setLocalSelection] = useState<string[]>([]);

  // Reset local selection when modal opens
  useEffect(() => {
    if (visible) {
      setLocalSelection(selectedValues);
    }
  }, [visible, selectedValues]);

  // Handle option toggle
  const handleToggle = useCallback(
    (value: string) => {
      if (!definition) return;

      if (definition.tagType === "single-select") {
        // Single select: replace selection or clear if same
        setLocalSelection((prev) => (prev.includes(value) ? [] : [value]));
      } else {
        // Multi select: toggle value
        setLocalSelection((prev) =>
          prev.includes(value)
            ? prev.filter((v) => v !== value)
            : [...prev, value]
        );
      }
    },
    [definition]
  );

  // Handle clear all
  const handleClear = useCallback(() => {
    setLocalSelection([]);
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    onSave(localSelection);
  }, [localSelection, onSave]);

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visible) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [visible, onClose]);

  if (!visible || !definition) {
    return null;
  }

  return (
    <div className="tag-selector-modal__overlay" onClick={handleOverlayClick}>
      <div
        className="tag-selector-modal__content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tag-selector-modal__header">
          <div className="tag-selector-modal__header-content">
            <h2 className="tag-selector-modal__title">{definition.tagName}</h2>
            <p className="tag-selector-modal__subtitle">
              {definition.tagType === "single-select"
                ? "Select one"
                : "Select all that apply"}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Options List */}
        <div className="tag-selector-modal__options">
          {definition.options.map((option) => {
            const isSelected = localSelection.includes(option);

            return (
              <button
                key={option}
                type="button"
                className={`tag-selector-modal__option ${
                  isSelected ? "tag-selector-modal__option--selected" : ""
                }`}
                onClick={() => handleToggle(option)}
                role={
                  definition.tagType === "single-select" ? "radio" : "checkbox"
                }
                aria-checked={isSelected}
              >
                <span className="tag-selector-modal__option-text">
                  {option}
                </span>
                {isSelected && (
                  <CheckIcon className="tag-selector-modal__checkmark" />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="tag-selector-modal__footer">
          {localSelection.length > 0 && (
            <button
              type="button"
              className="tag-selector-modal__clear-button"
              onClick={handleClear}
            >
              Clear selection
            </button>
          )}
          <button
            type="button"
            className="tag-selector-modal__save-button"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
